import { defineStore } from "pinia";
import type {
  SessionV2,
  WorkspaceUserGroupV1,
  WorkspaceV1
} from "@territoriesai/types";
import { JwtHelper } from "@/helpers/jwt.helper";
import { SessionHelper } from "@territoriesai/types/lib/helpers/session.helper.mjs";
import { type Router } from "vue-router";
import { useLoginWithRefreshToken } from "@/api/auth";

export const useSessionStore = defineStore("session", {
  state: (): {
    selectedSessionIndex: number;
    sessions: SessionV2[];
  } => {
    // Validate old sessions which will upgrade it to the latest version, and save it
    const savedSessions = localStorage.getItem("sessions");
    let sessions: SessionV2[] = [];

    if (savedSessions) {
      try {
        // Parse the sessions array
        sessions = JSON.parse(savedSessions);

        // Validate each session using SessionHelper.validate
        sessions = sessions.filter((session) => {
          const validationResult = SessionHelper.validate(session);
          if (!validationResult.valid) {
            console.warn(
              `TerritoriesAI: Invalid session encountered and excluded. Error: ${validationResult.invalidReason}`
            );
            return false;
          }
          return true;
        });
      } catch (error) {
        console.error(
          "TerritoriesAI: Error parsing sessions from local storage. Error [" +
            error +
            "]"
        );
      }
    }

    // Save the validated sessions back to local storage
    localStorage.setItem("sessions", JSON.stringify(sessions));

    let selectedSessionIndex: number = -1;
    if ("selected_session_index" in localStorage) {
      try {
        selectedSessionIndex = Number(
          localStorage.getItem("selected_session_index")
        );
      } catch (error) {
        console.error(
          "TerritoriesAI: Error retrieving selected session index from local storage. Error [" +
            error +
            "]"
        );
        selectedSessionIndex = -1;
      }
    }

    // Validate the index
    if (selectedSessionIndex < -1) {
      selectedSessionIndex = -1;
    }
    if (selectedSessionIndex >= 0 && selectedSessionIndex >= sessions.length) {
      selectedSessionIndex = -1;
    }

    // Do not remove expired tokens - nice to show list of previous users to select from

    // Update local storage with the validated results
    localStorage.setItem(
      "selected_session_index",
      String(selectedSessionIndex)
    );
    localStorage.setItem("sessions", JSON.stringify(sessions));

    return {
      selectedSessionIndex,
      sessions: sessions
    };
  },

  actions: {
    /**
     * This function will route to the sign in page, if there is no selected session, or if the selected session
     * cannot be refreshed.
     *
     * @param router
     */
    requiresSignIn(router: Router): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        if (this.sessions.length === 0) {
          router
            .push({ name: "SignIn" })
            .then(() => {
              resolve();
            })
            .catch((error) => {
              const errorMessage =
                "TerritoriesAI: Error checking required sign in. Cannot route to sign in. Error [" +
                error +
                "]";
              console.error(errorMessage);
              reject(new Error(errorMessage));
            });
        } else {
          if (this.selectedSessionIndex >= 0) {
            // A session is selected - is it valid?
            const currentSession = this.sessions[this.selectedSessionIndex];

            if (
              currentSession &&
              JwtHelper.isTokenValid(currentSession.idToken)
            ) {
              // No need to do anything - selected session is valid
              resolve();
            } else if (currentSession) {
              // Refresh the idToken - the selected session is expired
              const refreshMutation = useLoginWithRefreshToken(currentSession);

              refreshMutation.mutate(currentSession, {
                onSuccess: (data) => {
                  this.setSession(data.session);
                  router.go(0);
                  resolve();
                },
                onError: (error) => {
                  console.error(
                    "TerritoriesAI: Error checking required sign in. Cannot refresh session token. Error [" +
                      error +
                      "]"
                  );
                  router
                    .push({
                      name: "SignIn",
                      query: {
                        email: currentSession?.workspaceUserGroup.user?.email
                      }
                    })
                    .then(() => {
                      resolve();
                    })
                    .catch((error) => {
                      const errorMessage =
                        "TerritoriesAI: Error checking required sign in. Cannot route to sign in. Error [" +
                        error +
                        "]";
                      console.error(errorMessage);
                      reject(new Error(errorMessage));
                    });
                }
              });
            }
          } else {
            // No session is selected - route to the Sign in View
            router
              .push({ name: "SignIn" })
              .then(() => {
                resolve();
              })
              .catch((error) => {
                const errorMessage =
                  "TerritoriesAI: Error checking required sign in. Cannot route to sign in. Error [" +
                  error +
                  "]";
                console.error(errorMessage);
                reject(new Error(errorMessage));
              });
          }
        }
      });
    },

    /**
     * This function will route to the route name if there is a selected session - even if it is expired
     * For example, the About view will want to redirect to the home view if there is a selected session
     * @param router
     * @param routeName
     */
    routeIfSignedIn(router: Router, routeName: string): Promise<void> {
      return new Promise<void>((resolve, reject) => {
        if (this.sessions.length === 0) {
          // No sessions - not logged in
          resolve();
        } else {
          if (this.selectedSessionIndex >= 0) {
            // A session is selected - is it valid?
            const currentSession = this.sessions[this.selectedSessionIndex];

            if (
              currentSession &&
              JwtHelper.isTokenValid(currentSession.idToken)
            ) {
              // Selected session is valid - route to routeName
              router
                .push({ name: routeName })
                .then(() => {
                  resolve();
                })
                .catch((error) => {
                  const errorMessage =
                    "TerritoriesAI: Error routing if signed in. Cannot route to [" +
                    routeName +
                    "]. Error [" +
                    error +
                    "]";
                  console.error(errorMessage);
                  reject(new Error(errorMessage));
                });
            } else if (currentSession) {
              // Refresh the idToken - the selected session is expired
              const refreshMutation = useLoginWithRefreshToken(currentSession);

              refreshMutation.mutate(currentSession, {
                onSuccess: () => {
                  router
                    .push({ name: routeName })
                    .then(() => {
                      resolve();
                    })
                    .catch((error) => {
                      const errorMessage =
                        "TerritoriesAI: Error routing if signed in. Cannot route to [" +
                        routeName +
                        "]. Error [" +
                        error +
                        "]";
                      console.error(errorMessage);
                      reject(new Error(errorMessage));
                    });
                },
                onError: (error) => {
                  console.error(
                    "TerritoriesAI: Error routing if signed in. Cannot refresh session token. Error [" +
                      error +
                      "]"
                  );
                }
              });
            }
          } else {
            // No session is selected - do nothing
            resolve();
          }
        }
      });
    },

    selectedSession(): SessionV2 | null {
      try {
        if (this.selectedSessionIndex === -1) {
          return null;
        } else {
          return this.sessions[this.selectedSessionIndex] ?? null;
        }
      } catch (error) {
        console.error(
          "TerritoriesAI: Error retrieving selected session. Error [" +
            error +
            "]"
        );
        this.selectedSessionIndex = -1;
        return null;
      }
    },

    selectedUser(): SessionV2["workspaceUserGroup"]["user"] | null {
      try {
        if (this.selectedSessionIndex === -1) {
          return null;
        } else {
          return (
            this.sessions[this.selectedSessionIndex]?.workspaceUserGroup.user ??
            null
          );
        }
      } catch (error) {
        console.error(
          "TerritoriesAI: Error retrieving selected user. Error [" + error + "]"
        );
        this.selectedSessionIndex = -1;
        return null;
      }
    },

    selectedWorkspace(): SessionV2["workspaceUserGroup"]["workspace"] | null {
      try {
        if (this.selectedSessionIndex === -1) {
          return null;
        } else {
          return (
            this.sessions[this.selectedSessionIndex]?.workspaceUserGroup
              .workspace ?? null
          );
        }
      } catch (error) {
        console.error(
          "TerritoriesAI: Error retrieving selected workspace. Error [" +
            error +
            "]"
        );
        this.selectedSessionIndex = -1;
        return null;
      }
    },

    availableWorkspaces(): WorkspaceV1[] {
      if (this.selectedSessionIndex === -1) return [];
      if (this.sessions.length === 0) return [];
      const session = this.sessions[this.selectedSessionIndex];
      return (
        session?.workspaceUserGroup.user?.workspaceUserGroups?.map(
          (workspaceUserGroup: WorkspaceUserGroupV1) =>
            workspaceUserGroup.workspace as WorkspaceV1
        ) || []
      );
    },

    setSession(session: SessionV2): void {
      // Is this user already in the userTokens
      let found = false;
      for (let i = 0; i < this.sessions.length; i++) {
        if (
          this.sessions[i]!.workspaceUserGroup.id ===
          session.workspaceUserGroup.id
        ) {
          this.selectedSessionIndex = i;
          this.sessions[i] = session;
          found = true;
          break;
        }
      }

      if (!found) {
        this.sessions.push(session);
        this.selectedSessionIndex = this.sessions.length - 1;
      }

      localStorage.setItem(
        "selected_session_index",
        String(this.selectedSessionIndex)
      );
      localStorage.setItem("sessions", JSON.stringify(this.sessions));
    },

    isSignedIn(sessionIndex: number = -1): boolean {
      try {
        if (
          typeof sessionIndex !== "number" ||
          sessionIndex < 0 ||
          sessionIndex >= this.sessions.length
        ) {
          sessionIndex = this.selectedSessionIndex;
        }

        if (sessionIndex === -1) {
          return false;
        } else {
          const session = this.sessions[this.selectedSessionIndex];
          return session ? JwtHelper.isTokenValid(session.idToken) : false;
        }
      } catch (error) {
        console.error(
          "TerritoriesAI: Error checking if is signed in. Error [" + error + "]"
        );
        return false;
      }
    },

    signOut(): void {
      if (this.selectedSessionIndex >= 0) {
        this.sessions.splice(this.selectedSessionIndex, 1);
      }

      this.selectedSessionIndex = -1;

      localStorage.setItem(
        "selected_session_index",
        String(this.selectedSessionIndex)
      );
      localStorage.setItem("sessions", JSON.stringify(this.sessions));
    },

    signOutAll(): void {
      this.selectedSessionIndex = -1;
      this.sessions = [];

      localStorage.setItem(
        "selected_session_index",
        String(this.selectedSessionIndex)
      );
      localStorage.setItem("sessions", JSON.stringify(this.sessions));
    }
  }
});
